import { useLazyQuery } from '@apollo/client';
import { CheckCircle, QueuePlayNext, Router } from '@mui/icons-material';
import {
  AlertTitle,
  Box,
  Button,
  Collapse,
  Grid,
  Alert as MuiAlert,
  Paper,
  Typography,
  styled,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useActivateDevice } from '~/api/devices';
import { ButtonLink, Link } from '~/components/link';
import { PageContainer } from '~/components/page-layout';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { assert } from '~/lib/assert';
import { supportEmail } from '~/lib/constants';
import { DeviceRegistrationDocument } from './activate.generated';
import { CodeInput, validateCode } from './components';

const Alert = styled(MuiAlert)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  '.MuiAlert-icon': {
    fontSize: '2rem',
  },
}));

export const ActivateDevice = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const { currentNetwork } = useAppContext();

  const link = useLink();

  const [lookup, registration] = useLazyQuery(DeviceRegistrationDocument);

  const [activate, activation] = useActivateDevice();

  const [submitted, setSubmitted] = useState(false);

  const [code, setCode] = useState(validateCode(searchParams.get('code')));

  useEffect(() => {
    if (code !== undefined) void lookup({ variables: { code } });
  }, [code, lookup]);

  useEffect(() => {
    if (activation.error !== undefined) setSubmitted(false);
  }, [activation.error]);

  return (
    <Box>
      <Toolbar
        titleIcon={<Router />}
        titleText={t('pages.devices.activate.title')}
        actions={
          activation.data?.activateDevice?.device == null ? (
            <Button
              fullWidth
              color="primary"
              disabled={registration.data?.deviceRegistration == null || !code || submitted}
              onClick={() => {
                setSubmitted(true);
                assert(typeof code === 'string');
                void activate({
                  variables: { input: { code, networkId: currentNetwork.id } },
                });
              }}
              size="large"
              startIcon={<QueuePlayNext />}
              variant="contained"
            >
              {t('pages.devices.activate.activate_button')}
            </Button>
          ) : (
            <ButtonLink
              color="primary"
              replace
              size="large"
              startIcon={<Router />}
              sx={(theme) => ({ fontSize: '1rem', padding: theme.spacing(1, 2) })}
              to={link(`/devices/${activation.data.activateDevice.device.id}/details`)}
              variant="contained"
            >
              {t('pages.devices.activate.view_device')}
            </ButtonLink>
          )
        }
      />

      <PageContainer>
        {activation.data?.activateDevice?.device == null ? (
          <Paper
            sx={{
              padding: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box
              sx={{
                width: '100%',
              }}
            >
              <Collapse in={activation.error !== undefined}>
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" onClick={() => window.location.reload()}>
                      {t('pages.devices.activate.reload')}
                    </Button>
                  }
                >
                  <AlertTitle color="inherit">{t('common.messages.error')}</AlertTitle>
                  <Typography color="inherit">
                    {activation.error?.message || t('pages.devices.activate.error_message')}
                  </Typography>
                </Alert>
              </Collapse>
              <Collapse in={registration.called && !registration.loading && code != null}>
                <Alert
                  action={
                    registration.data?.deviceRegistration == null && (
                      <Button color="inherit" onClick={() => setCode(undefined)}>
                        {t('pages.devices.activate.try_again')}
                      </Button>
                    )
                  }
                  severity={registration.data?.deviceRegistration == null ? 'error' : 'info'}
                >
                  <AlertTitle color="inherit">
                    {registration.data?.deviceRegistration
                      ? `${t('pages.devices.activate.activating')} ${
                          registration.data.deviceRegistration.type
                        } (${registration.data.deviceRegistration.keyIdDescription}: ${
                          registration.data.deviceRegistration.keyId
                        })`
                      : t('pages.devices.activate.device_not_found')}
                  </AlertTitle>
                  <Typography color="inherit">
                    {registration.data?.deviceRegistration &&
                      `${t('pages.devices.activate.not_your_device')} `}
                    <Link
                      color="secondary"
                      to={`mailto:${supportEmail}`}
                      title={t('pages.devices.activate.contact_support')}
                    >
                      {t('pages.devices.activate.contact_support')}
                    </Link>
                  </Typography>
                </Alert>
              </Collapse>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              <Typography variant="h4">{t('pages.devices.activate.enter_code')}</Typography>
              <CodeInput code={code} disabled={submitted} onChange={setCode} />
            </Box>
          </Paper>
        ) : (
          <Paper sx={{ padding: 1 }}>
            <Grid alignItems="center" container py={4} spacing={2} textAlign="center">
              <Grid item xs={12}>
                <Typography component="h2" gutterBottom align="center" variant="h1">
                  {t('pages.devices.activate.congratulations')}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography align="center" variant="h4" sx={{ fontWeight: 400 }}>
                  {t('pages.devices.activate.success_message')}{' '}
                  {activation.data.activateDevice.device.type}{' '}
                  {t('pages.devices.activate.device_label')}{' '}
                  <strong>{activation.data.activateDevice.device.keyId}</strong>
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <CheckCircle color="primary" sx={{ width: 150, height: 150 }} />
              </Grid>
            </Grid>
          </Paper>
        )}
      </PageContainer>
    </Box>
  );
};
